/* eslint-disable react/prop-types */
import React, { useState } from 'react';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table/Table';
import TableBody from '@mui/material/TableBody/TableBody';
import TableCell from '@mui/material/TableCell/TableCell';
import TableHead from '@mui/material/TableHead/TableHead';
import TableRow from '@mui/material/TableRow/TableRow';
import Typography from '@mui/material/Typography/Typography';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import QTip from 'components/QuickenControls/QTip';

import AddShareDialog from './AddShareDialog';
import ManageSharesMenu from './ManageSharesMenu';
import StatusChip from './StatusChip';
import { getNameData, getStatusText } from './helpers';
import useShareCounts from './useShareCounts';

const StyledCell = (props) => {
  const { children, sx = {} } = props;
  return (
    <TableCell
      sx={{
        borderBottom: '1px solid #dfdfdf',
        ...sx,
      }}
    >
      {children}
    </TableCell>
  );
};

const ManageSharesDialog = (props) => {
  const { dataset, shares, onClose } = props;

  const [showAddShareDialog, setShowAddShareDialog] = useState(false);

  const [sharesUsed, sharesAllowed] = useShareCounts(shares);
  const sharesLeft = Math.max(sharesAllowed - sharesUsed, 0);

  const handleAddShare = () => setShowAddShareDialog(true);

  const renderAvatar = (initials, index) => (
    <Avatar
      aria-label={initials}
      sx={(theme) => ({
        bgcolor: theme.palette.data.sequence[index],
        '&:hover': {
          border: '1px solid white',
        },
        typography: 'body2',
        height: 32,
        width: 32,
      })}
    >
      {initials}
    </Avatar>
  );

  const renderOwner = () => {
    const nameData =
      shares && getNameData(shares[0].grantorFirstName, shares[0].grantorLastName, shares[0].grantorEmail);
    return (
      <TableRow key={dataset.id}>
        <StyledCell>{nameData ? renderAvatar(nameData.initials, 0) : <>&nbsp;</>}</StyledCell>
        <StyledCell>
          <Typography noWrap variant='paragraphRegular'>
            You
          </Typography>
        </StyledCell>
        <StyledCell>
          <Typography noWrap variant='paragraphRegular'>
            &nbsp;
          </Typography>
        </StyledCell>
        <StyledCell>
          <Typography color='text.secondary' noWrap variant='paragraphRegular'>
            Owner
          </Typography>
        </StyledCell>
        <StyledCell>
          <Typography color='text.secondary' noWrap variant='paragraphRegular'>
            &nbsp;
          </Typography>
        </StyledCell>
        <StyledCell>
          <Typography color='text.secondary' noWrap variant='paragraphRegular'>
            &nbsp;
          </Typography>
        </StyledCell>
      </TableRow>
    );
  };

  const renderShare = (share, index) => {
    const nameData = getNameData(share.granteeFirstName, share.granteeLastName, share.granteeEmail);
    const status = getStatusText(share);
    return (
      <TableRow key={share.id}>
        <StyledCell>{renderAvatar(nameData.initials, index + 1)}</StyledCell>
        <StyledCell>
          <QTip title={nameData.name} wrapOnly>
            <Typography noWrap variant='paragraphRegular' sx={{ maxWidth: 125 }}>
              {nameData.name}
            </Typography>
          </QTip>
        </StyledCell>
        <StyledCell>
          <QTip title={share.granteeEmail} wrapOnly>
            <Typography noWrap variant='paragraphRegular' sx={{ maxWidth: 160 }}>
              {share.granteeEmail}
            </Typography>
          </QTip>
        </StyledCell>
        <StyledCell>
          <Typography color='text.secondary' variant='paragraphRegular'>
            Full access
          </Typography>
        </StyledCell>
        <StyledCell>
          {share.state === 'ACTIVE' ? (
            <Typography color='text.secondary' variant='paragraphRegular'>
              {status}
            </Typography>
          ) : (
            <StatusChip label={status} />
          )}
        </StyledCell>
        <StyledCell>
          <ManageSharesMenu dataset={dataset} share={share} />
        </StyledCell>
      </TableRow>
    );
  };

  return (
    <StdDialog disableBackdropClick onClose={onClose} open showCloseButton size='md' title='Manage users'>
      <Box display='flex' flex='1' flexDirection='column'>
        <StdDialogContent id='manage_shares_dialog_content'>
          {showAddShareDialog && (
            <AddShareDialog
              dataset={dataset}
              onClose={() => setShowAddShareDialog(false)}
              shareCounts={[sharesUsed, sharesAllowed]}
            />
          )}
          <Typography variant='displayRegular'>{dataset.name}</Typography>
          <Typography variant='captionRegular' sx={{ color: 'text.secondary' }}>
            {`${sharesLeft}/${sharesAllowed} invitations left`}
          </Typography>
          <Table
            aria-label='shares'
            sx={{
              minWidth: 500,
              mt: 3,
            }}
          >
            <TableHead>
              <TableRow>
                <StyledCell>
                  <Typography variant='captionRegular'>&nbsp;</Typography>
                </StyledCell>
                <StyledCell sx={{ width: 125 }}>
                  <Typography variant='captionRegular'>Name</Typography>
                </StyledCell>
                <StyledCell>
                  <Typography variant='captionRegular'>Email</Typography>
                </StyledCell>
                <StyledCell sx={{ width: 125 }}>
                  <Typography noWrap variant='captionRegular'>
                    Role/Permission
                  </Typography>
                </StyledCell>
                <StyledCell sx={{ width: 125 }}>
                  <Typography variant='captionRegular'>Last active</Typography>
                </StyledCell>
                <StyledCell sx={{ width: 75 }}>
                  <Typography variant='captionRegular'>&nbsp;</Typography>
                </StyledCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderOwner()}
              {shares && shares.map((share, index) => renderShare(share, index))}
            </TableBody>
          </Table>
        </StdDialogContent>
        <StdDialogActions
          primary={{
            type: 'none',
          }}
          tertiaryAction={{
            disabled: !sharesLeft,
            children: `+ Invite ${!!sharesUsed && !!sharesLeft ? `(${sharesLeft})` : ''}`,
            onClick: handleAddShare,
          }}
        />
      </Box>
    </StdDialog>
  );
};

export default ManageSharesDialog;
